// PageHeader — page-level heading shell.
//
// Mirrors `.page-header`, `.page-header--solo`, `.page-header__inner`,
// `.page-header__title-row`, and `.page-title` in
// /Users/harry/Downloads/employee-portal/project/styles.css (lines 621-652).
//
// Layout:
//   .page-header (bg neutral.25, padding-top 24)
//     .page-header__inner (max-width 1440, margin 0 auto, padding 0 40)
//       .page-header__title-row (flex space-between, padding 24 0)
//         h1.page-title + optional right slot
//       optional Tabs (sit below title row, still inside inner)
//
// Variants:
//   - "default": no border (intended to sit above tabs which provide their own
//     separator)
//   - "solo": adds bottom border + 24px bottom padding (no tabs)

import { Box, HStack, Heading } from "@chakra-ui/react";
import { ReactNode } from "react";

export interface PageHeaderProps {
  /** Page title (rendered as h1 with the .page-title display style). */
  title: ReactNode;
  /** Optional right-side slot — typically a primary action button. */
  rightSlot?: ReactNode;
  /** Optional Tabs (or tab-row) rendered below the title row inside the inner. */
  tabs?: ReactNode;
  /**
   * Visual variant.
   * - "default" — no bottom border (pair with `tabs` for the separator).
   * - "solo" — adds bottom border + 24px bottom padding (no tabs).
   */
  variant?: "default" | "solo";
}

const PageHeader = ({
  title,
  rightSlot,
  tabs,
  variant = "default",
}: PageHeaderProps) => {
  const isSolo = variant === "solo";

  return (
    <Box
      as="header"
      bg="neutral.25"
      pt="1xl"
      pb={isSolo ? "1xl" : undefined}
      borderBottomWidth={isSolo ? "1px" : undefined}
      borderBottomStyle={isSolo ? "solid" : undefined}
      borderBottomColor={isSolo ? "neutral.200" : undefined}
    >
      <Box maxW="1440px" mx="auto" px="3xl">
        <HStack
          justify="space-between"
          align="center"
          spacing="lg"
          py="1xl"
        >
          <Heading
            as="h1"
            m={0}
            fontFamily="heading"
            fontWeight={500}
            fontSize="3xl"
            lineHeight="1"
            letterSpacing="-0.01em"
            color="neutral.1000"
          >
            {title}
          </Heading>
          {rightSlot ? <Box>{rightSlot}</Box> : null}
        </HStack>
        {tabs ? <Box>{tabs}</Box> : null}
      </Box>
    </Box>
  );
};

PageHeader.displayName = "PageHeader";

export default PageHeader;
